"use client";

import { useEffect, useState } from "react";

import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useStrikes } from "#/hooks/useStrikes";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import { type OptionType } from "#/types/options";
import { nextMaturities } from "#/utils/maturities";

import { showComboWarningToast } from "../StrategySelector.utils";

export const useSpread = (optionType: OptionType) => {
  const {
    comboPerpInstruments,
    comboOptionInstruments,
    handleAddLegs,
    buildInstrument,
  } = useComboInstrumentsController();
  const { marketIndexPrice } = useMarketIndexPrice();

  const [maturityIndex, setMaturityIndex] = useState(0);

  const maturities = nextMaturities();
  const maturity = maturities[maturityIndex];
  const { strikes } = useStrikes(maturity);

  const isCall = optionType === "call";

  const applySpread = () => {
    // Use nearest strikes above and below market index price for the spread
    let lowerStrike =
      strikes.toReversed().find((s) => s <= marketIndexPrice) || strikes.at(0);
    let upperStrike =
      strikes.find((s) => s > marketIndexPrice) || strikes.at(1);

    if (
      lowerStrike === undefined ||
      upperStrike === undefined ||
      lowerStrike === upperStrike
    ) {
      if (maturityIndex + 1 < maturities.length) {
        setMaturityIndex(maturityIndex + 1);
        return;
      }

      showComboWarningToast();
      return;
    }

    if (lowerStrike > upperStrike) {
      [lowerStrike, upperStrike] = [upperStrike, lowerStrike];
    }

    // For calls: creates bull call spread (buy lower, sell higher)
    // For puts: creates bull put spread (sell lower, buy higher)
    // Users can flip directions to create bear spreads
    handleAddLegs([
      buildInstrument({
        type: "options",
        side: isCall ? "buy" : "sell",
        optionType,
        strike: lowerStrike,
        maturity,
      }),
      buildInstrument({
        type: "options",
        side: isCall ? "sell" : "buy",
        optionType,
        strike: upperStrike,
        maturity,
      }),
    ]);
  };

  useEffect(() => {
    if (maturityIndex > 0) {
      // Retry spread on maturity change to test all maturities
      applySpread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only rerun when maturityIndex changes
  }, [maturityIndex]);

  const isActivePatternSpread = () => {
    if (comboPerpInstruments.length > 0 || comboOptionInstruments.length !== 2)
      return false;

    const [a, b] = comboOptionInstruments.toSorted(
      (x, y) => x.strike - y.strike,
    );

    if (
      a.maturity !== b.maturity ||
      a.underlying !== b.underlying ||
      a.optionType !== optionType ||
      b.optionType !== optionType ||
      a.strike === b.strike
    ) {
      return false;
    }

    // Supports both bull spreads and bear spreads (opposite sides required)
    return a.side !== b.side;
  };

  return { applySpread, isActivePatternSpread };
};
